import React from 'react';
import { Button } from './ui/button';
import { Phone, Clock, MapPin, Key, Car } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Hero = ({ currentLang, translations }) => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [cardRef, cardVisible] = useScrollAnimation();
  
  return (
    <section className="relative py-20 overflow-hidden">
      {/* Hero Background Image */}
      <div 
        className="absolute inset-0 z-0 opacity-30"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_auto-locksmith/artifacts/tvt5z2vx_20250924_0130_Car%20Key%20Focus_simple_compose_01k5wb30bkfv99ewtcszb2d18b%20%281%29.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          willChange: 'transform'
        }}
        role="img"
        aria-label="Professional car keys background"
      ></div>
      
      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/60 to-blue-50/80 z-10"></div>
      
      <div className="container mx-auto px-4 relative z-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div 
              ref={heroRef}
              className={`space-y-6 lg:space-y-8 text-center lg:text-left scroll-animate-left ${heroVisible ? 'visible' : ''}`}
            >
              <div className="space-y-4">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 leading-tight">
                  {translations[currentLang].hero.title}
                </h1>
                <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
                  {translations[currentLang].hero.subtitle}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button 
                  size="lg"
                  className="bg-red-600 hover:bg-red-700 text-white px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg flex items-center justify-center space-x-3 rtl:space-x-reverse shadow-lg hover:shadow-xl transition-all w-full sm:w-auto"
                  onClick={() => window.open('tel:+972553002025')}
                >
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>{translations[currentLang].hero.emergency}</span>
                </Button>
                
                <Button 
                  size="lg"
                  variant="outline"
                  className="px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white transition-all w-full sm:w-auto"
                  onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  {translations[currentLang].hero.services}
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6 pt-6 lg:pt-8">
                <div className="flex items-center space-x-3 rtl:space-x-reverse p-3 lg:p-4 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm">
                  <Clock className="w-6 h-6 lg:w-8 lg:h-8 text-green-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm lg:text-base">{translations[currentLang].hero.available24}</p>
                    <p className="text-xs lg:text-sm text-gray-600">{translations[currentLang].hero.emergency}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 rtl:space-x-reverse p-3 lg:p-4 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm">
                  <MapPin className="w-6 h-6 lg:w-8 lg:h-8 text-blue-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm lg:text-base">{translations[currentLang].hero.location}</p>
                    <p className="text-xs lg:text-sm text-gray-600 truncate">Zunz 30, {translations[currentLang].hero.telAviv}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 rtl:space-x-reverse p-3 lg:p-4 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm">
                  <Key className="w-6 h-6 lg:w-8 lg:h-8 text-orange-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm lg:text-base">{translations[currentLang].hero.expert}</p>
                    <p className="text-xs lg:text-sm text-gray-600">{translations[currentLang].hero.professional}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative mt-8 lg:mt-0">
              <div 
                ref={cardRef}
                className={`bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-6 lg:p-8 relative z-10 scroll-animate-right ${cardVisible ? 'visible' : ''}`}
              >
                <div className="text-center space-y-4 lg:space-y-6">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center mx-auto">
                    <Car className="w-8 h-8 lg:w-10 lg:h-10 text-white" />
                  </div>
                  
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900">
                    {translations[currentLang].hero.contactCard.title}
                  </h3>
                  
                  <div className="space-y-3 lg:space-y-4">
                    <div className="flex items-center justify-center space-x-3 rtl:space-x-reverse">
                      <Phone className="w-4 h-4 lg:w-5 lg:h-5 text-red-600" />
                      <a href="tel:+972553002025" className="text-lg lg:text-xl font-bold text-gray-900 hover:text-red-600 transition-colors">
                        +972-55-300-2025
                      </a>
                    </div>
                    
                    <div className="flex items-center justify-center space-x-3 rtl:space-x-reverse">
                      <MapPin className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
                      <span className="text-sm lg:text-base text-gray-700">Zunz 30, {translations[currentLang].hero.telAviv}</span>
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-3 text-base lg:text-lg"
                    onClick={() => window.open('tel:+972553002025')}
                  >
                    {translations[currentLang].hero.contactCard.callNow}
                  </Button>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-72 h-72 bg-blue-100 rounded-full opacity-20 -z-10"></div>
              <div className="absolute -bottom-4 -left-4 w-64 h-64 bg-red-100 rounded-full opacity-20 -z-10"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;